import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import logo from '../assets/logo.png';
import $ from 'jquery';
import 'datatables.net';
import 'datatables.net-dt';
import './GSTExemptionsViewer.css';

const GSTExemptionsViewer = () => {
    const tableRef = useRef(null);
    const dataTableRef = useRef(null);
    const initRef = useRef(false);
    const exemptionsDataRef = useRef([]);
    const isMounted = useRef(false);
    const [sectionFilter, setSectionFilter] = useState('');
    const [descriptionFilter, setDescriptionFilter] = useState('');
    const [exemptionCodeFilter, setExemptionCodeFilter] = useState('');
    const [exemptionsData, setExemptionsData] = useState([]);
    const isDrawing = useRef(false);
    const [isTableInitialized, setIsTableInitialized] = useState(false);
    const [isProDropdownOpen, setIsProDropdownOpen] = useState(false);
    const navigate = useNavigate();

    const expectedHeaders = useMemo(() => [
        'Section',
        'Exemption Code',
        'Category',
        'Description'
    ], []);

    const applyStyles = () => {
        const isDarkMode = $('body').hasClass('dark-mode');
        $('.custom-filter-input, .section-filter-select').css({
            height: '35px',
            borderRadius: '6px',
            padding: '8px 12px',
            width: '200px',
            fontSize: '14px',
            border: '1px solid ' + (isDarkMode ? '#777' : '#ced4da'),
            background: isDarkMode ? '#555' : '#fff',
            color: isDarkMode ? '#fff' : '#495057',
            boxSizing: 'border-box',
            lineHeight: '1.5',
            marginRight: '10px'
        });
        $('.dt-paging button.dt-paging-button').css({
            padding: '8px 12px',
            fontSize: '14px',
            fontWeight: '500',
            border: '1px solid ' + (isDarkMode ? '#777' : '#ced4da'),
            borderRadius: '6px',
            backgroundColor: isDarkMode ? '#555' : '#fff',
            color: isDarkMode ? '#ccc' : '#495057',
            cursor: 'pointer',
            margin: '0 4px',
            boxSizing: 'border-box',
            transition: 'background-color 0.2s, color 0.2s'
        });
        $('.dt-paging button.dt-paging-button:not(.disabled):hover').css({
            backgroundColor: isDarkMode ? '#666' : '#f0f4f8',
            color: isDarkMode ? '#fff' : '#007bff'
        });
        $('.dt-paging button.dt-paging-button.current').css({
            backgroundColor: isDarkMode ? '#007bff' : '#007bff',
            color: '#fff',
            borderColor: isDarkMode ? '#007bff' : '#007bff'
        });
        $('.dt-paging button.dt-paging-button.disabled').css({
            cursor: 'not-allowed',
            opacity: '0.5'
        });
        $('#exemptionsTable').addClass('table-visible');
    };

    useEffect(() => {
        if (isMounted.current) return;
        isMounted.current = true;

        const loadExemptionsData = async () => {
            if (exemptionsDataRef.current.length > 0) {
                return;
            }

            try {
                const response = await fetch('/gst_exemptions.json');
                if (!response.ok) throw new Error('Failed to fetch GST exemptions data');
                const jsonData = await response.json();
                const exemptions = [];

                // Flatten the exemptions from the JSON structure
                Object.keys(jsonData.exemptions).forEach(section => {
                    jsonData.exemptions[section].forEach(item => {
                        exemptions.push({
                            Section: section,
                            'Exemption Code': item.exemption_code,
                            Category: item.category,
                            Description: item.description
                        });
                    });
                });

                exemptionsDataRef.current = exemptions;
                setExemptionsData(exemptions);
            } catch (error) {
                console.error('Error loading GST exemptions data:', error);
                alert('Failed to load GST exemptions data. Please try again later.');
                exemptionsDataRef.current = [];
                setExemptionsData([]);
            }

            if (!tableRef.current) return;

            if (initRef.current && dataTableRef.current) {
                try {
                    dataTableRef.current.destroy(false);
                } catch (error) {
                    console.error('Error destroying DataTable:', error);
                }
                initRef.current = false;
            }

            applyStyles();

            if (!$.fn.DataTable.isDataTable(tableRef.current)) {
                try {
                    dataTableRef.current = $(tableRef.current).DataTable({
                        columns: expectedHeaders.map(header => ({
                            title: header,
                            data: header,
                            render: (data, type, row, meta) => {
                                return data || '';
                            }
                        })),
                        columnDefs: [
                            { width: '150px', targets: 0 },
                            { width: '120px', targets: 1 },
                            { width: '150px', targets: 2 },
                            { width: 'auto', targets: 3 }
                        ],
                        pageLength: 15,
                        lengthMenu: [15, 25, 50, 100],
                        searching: true,
                        ordering: true,
                        order: [], // Preserve original order from JSON
                        responsive: true,
                        language: {
                            search: '',
                            searchPlaceholder: 'Filter records...',
                            lengthMenu: 'Show _MENU_ entries',
                        },
                        dom: '<"table-header"l>t<"table-footer"ip>',
                        deferRender: true
                    });
                    initRef.current = true;
                    dataTableRef.current.clear().rows.add(exemptionsDataRef.current).draw();
                    dataTableRef.current.on('draw', () => {
                        applyStyles();
                        isDrawing.current = false;
                    });
                    setIsTableInitialized(true);
                } catch (error) {
                    console.error('Error initializing DataTable:', error);
                    alert('Failed to initialize table.');
                }
            }
        };

        loadExemptionsData();

        return () => {
            if (dataTableRef.current && !document.getElementById('exemptionsTable')) {
                try {
                    dataTableRef.current.destroy(false);
                } catch (error) {
                    console.error('Error destroying DataTable:', error);
                }
            }
            $.fn.dataTable.ext.search = [];
            isMounted.current = false;
        };
    }, [expectedHeaders]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            const dropdown = document.querySelector('.pro-dropdown');
            if (dropdown && !dropdown.contains(event.target)) {
                setIsProDropdownOpen(false);
            }
        };

        if (isProDropdownOpen) {
            document.addEventListener('click', handleClickOutside, { capture: true });
        }

        return () => {
            document.removeEventListener('click', handleClickOutside, { capture: true });
        };
    }, [isProDropdownOpen]);

    const sectionOptions = useMemo(() => {
        const sections = [...new Set(exemptionsData.map(row => row['Section']))].filter(section => section).sort();
        return ['', ...sections];
    }, [exemptionsData]);

    useEffect(() => {
        if (!dataTableRef.current || !isTableInitialized) return;

        const timeoutId = setTimeout(() => {
            if (isDrawing.current) return;
            isDrawing.current = true;

            $.fn.dataTable.ext.search = [];

            const filterFunction = (settings, data, dataIndex, rowData, counter) => {
                if (settings.nTable.id !== 'exemptionsTable') return true;
                const section = String(data[0] || '').toLowerCase();
                const exemptionCode = String(data[1] || '').toLowerCase();
                const description = String(data[3] || '').toLowerCase();
                const sectionFilterValue = sectionFilter.toLowerCase().trim();
                const exemptionCodeFilterValue = exemptionCodeFilter.toLowerCase().trim();
                const descriptionFilterValue = descriptionFilter.toLowerCase().trim();

                let sectionMatch = true;
                let exemptionCodeMatch = true;
                let descriptionMatch = true;

                if (sectionFilterValue) {
                    sectionMatch = section === sectionFilterValue;
                }

                if (exemptionCodeFilterValue) {
                    exemptionCodeMatch = exemptionCode.includes(exemptionCodeFilterValue);
                }

                if (descriptionFilterValue) {
                    descriptionMatch = description.includes(descriptionFilterValue);
                }

                return sectionMatch && exemptionCodeMatch && descriptionMatch;
            };

            $.fn.dataTable.ext.search.push(filterFunction);

            try {
                dataTableRef.current
                    .column(0)
                    .search('')
                    .column(1)
                    .search('')
                    .column(3)
                    .search('')
                    .draw('full-reset');
            } catch (error) {
                console.error('Error during DataTable filter draw:', error);
                isDrawing.current = false;
            }
        }, 400);

        return () => clearTimeout(timeoutId);
    }, [sectionFilter, descriptionFilter, exemptionCodeFilter, isTableInitialized]);

    const handleSectionFilterChange = useCallback((e) => {
        setSectionFilter(e.target.value);
    }, []);

    const handleDescriptionFilterChange = useCallback((e) => {
        setDescriptionFilter(e.target.value);
    }, []);

    const handleExemptionCodeFilterChange = useCallback((e) => {
        setExemptionCodeFilter(e.target.value);
    }, []);

    const handleLogout = () => {
        signOut(auth)
            .then(() => {
                navigate('/');
            })
            .catch((err) => console.error('Logout error:', err));
    };

    const resetTable = useCallback(() => {
        if (dataTableRef.current) {
            setSectionFilter('');
            setExemptionCodeFilter('');
            setDescriptionFilter('');
            dataTableRef.current
                .column(0)
                .search('')
                .column(1)
                .search('')
                .column(3)
                .search('')
                .draw();
            dataTableRef.current.page(0).draw('page');
            applyStyles();
        }
    }, []);

    return (
        <div className="exemptions-viewer-container">
            <header className="header">
                <div className="header-content">
                    <img src={logo} alt="Customs Pro AI Logo" />
                    <h1>GST Exemption Codes</h1>
                    <nav className="nav">
                        <Link to="/" className="nav-item">Home</Link>
                        <Link to="/app" className="nav-item">Classify Goods</Link>
                        <Link to="/customs-chat" className="nav-item">Customs Chat</Link>
                        <div
                            className={`nav-item pro-dropdown ${isProDropdownOpen ? 'open' : ''}`}
                            onClick={() => setIsProDropdownOpen(!isProDropdownOpen)}
                            role="button"
                            aria-expanded={isProDropdownOpen}
                            aria-controls="pro-dropdown-menu"
                        >
                            <span className="active">Pro</span>
                            <div className="dropdown-content" id="pro-dropdown-menu" onClick={(e) => e.stopPropagation()}>
                                <Link to="/tariff-viewer" className="dropdown-item">Tariff Viewer</Link>
                                <Link to="/tco-viewer" className="dropdown-item">TCO Viewer</Link>
                                <Link to="/precedent-viewer" className="dropdown-item">Precedent Viewer</Link>
                                <Link to="/ahecc-viewer" className="dropdown-item">AHECC Viewer</Link>
                                <Link to="/schedule4-viewer" className="dropdown-item">Schedule 4 Viewer</Link>
                                <Link to="/refund-reason-viewer" className="dropdown-item">Refund Reason Codes</Link>
                                <Link to="/gst-exemptions-viewer" className="dropdown-item active">GST Exemptions Viewer</Link>
                                <Link to="/customs-depots-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Customs Depot Codes</Link>
                                <Link to="/quarantine-depots-viewer" className="dropdown-item"onClick={() => setIsProDropdownOpen(false)}>Quarantine Depot Codes</Link>
                                <Link to="/history" className="dropdown-item">History</Link>
                            </div>
                        </div>
                        <Link to="/settings" className="nav-item">Settings</Link>
                        <span className="nav-item logout" onClick={handleLogout}>Logout</span>
                    </nav>
                </div> 
            </header>
            <div className="main-content">
                <div className="table-panel">
                    <div className="table-header">
                        <div id="customFilter">
                            <select
                                value={sectionFilter}
                                onChange={handleSectionFilterChange}
                                className="section-filter-select"
                            >
                                <option value="">All Sections</option>
                                {sectionOptions.map(section => (
                                    <option key={section} value={section}>{section}</option>
                                ))}
                            </select>
                            <input
                                type="text"
                                placeholder="Filter Exemption Code..."
                                value={exemptionCodeFilter}
                                onChange={handleExemptionCodeFilterChange}
                                className="custom-filter-input"
                            />
                            <input
                                type="text"
                                placeholder="Filter Description..."
                                value={descriptionFilter}
                                onChange={handleDescriptionFilterChange}
                                className="custom-filter-input"
                            />
                        </div>
                        <button id="resetTableBtn" onClick={resetTable}>Reset Table</button>
                    </div>
                    <table id="exemptionsTable" className="display" ref={tableRef}></table>
                </div>
            </div>
        </div>
    );
};

export default GSTExemptionsViewer;